import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { submitBooking } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { PROGRAM_OPTIONS, BRAND } from "@/data/content";

export default function BookingModal({ open, onOpenChange, defaultProgram }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    program: defaultProgram || PROGRAM_OPTIONS[1],
    preferred_date: "",
    goals: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm((p) => ({
        ...p,
        program: defaultProgram || PROGRAM_OPTIONS[1],
      }));
    }
  }, [open, defaultProgram]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.program) {
      toast.error("Name, email and program are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitBooking(form);
      trackEvent("booking_submit", {
        program: form.program,
        source: "booking_modal",
      });
      setDone(true);
      toast.success("Application sent.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Booking failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="bg-ng-surface border border-ng-blue/40 text-white max-w-xl rounded-none p-0 max-h-[90vh] overflow-y-auto shadow-[0_0_80px_-20px_rgba(30,144,255,0.6)]"
      >
        <div className="p-8 md:p-10">
          {done ? (
            <div data-testid="booking-success" className="text-center py-6">
              <CheckCircle2 size={48} strokeWidth={1.5} className="text-ng-blue mx-auto mb-6" />
              <DialogHeader>
                <DialogTitle className="font-display font-bold uppercase text-3xl text-white">
                  Application sent
                </DialogTitle>
                <DialogDescription className="text-white/65 mt-3 font-light">
                  Coach J received your interest in{" "}
                  <span className="text-ng-blue">{form.program}</span>. You&rsquo;ll
                  get a reply within 24h to set up your strategy call.
                </DialogDescription>
              </DialogHeader>
              <button
                data-testid="booking-close"
                onClick={() => onOpenChange(false)}
                className="mt-8 inline-flex items-center gap-2 bg-ng-blue text-white px-8 py-3 uppercase tracking-[0.22em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="label-eyebrow mb-3">Apply for Coaching</div>
                <DialogTitle className="font-display font-bold uppercase text-3xl text-white tracking-tight">
                  Book a strategy call
                </DialogTitle>
                <DialogDescription className="text-white/65 mt-2 font-light">
                  30-minute call. Free. Coach J will assess fit, goals and
                  recommend the right package. Or text or call Coach J at {BRAND.phone}.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="mt-8 space-y-6">
                <Input
                  label="Full Name"
                  testId="booking-name"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  required
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Email"
                    type="email"
                    testId="booking-email"
                    value={form.email}
                    onChange={(v) => update("email", v)}
                    required
                  />
                  <Input
                    label="Phone"
                    testId="booking-phone"
                    value={form.phone}
                    onChange={(v) => update("phone", v)}
                  />
                </div>

                <div>
                  <label className="label-eyebrow block mb-2">Package</label>
                  <select
                    data-testid="booking-program"
                    value={form.program}
                    onChange={(e) => update("program", e.target.value)}
                    className="w-full bg-transparent border-b border-ng-line text-white py-3 focus:outline-none focus:border-ng-blue transition-colors font-display"
                  >
                    {PROGRAM_OPTIONS.map((p) => (
                      <option key={p} value={p} className="bg-ng-surface">
                        {p}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  label="Preferred Date / Time"
                  testId="booking-date"
                  placeholder="e.g. Mon afternoon PST"
                  value={form.preferred_date}
                  onChange={(v) => update("preferred_date", v)}
                />

                <div>
                  <label className="label-eyebrow block mb-2">
                    Goals / context
                  </label>
                  <textarea
                    data-testid="booking-goals"
                    rows={3}
                    value={form.goals}
                    onChange={(e) => update("goals", e.target.value)}
                    placeholder="Where you are now and where you want to be..."
                    className="w-full bg-transparent border-b border-ng-line text-white py-3 focus:outline-none focus:border-ng-blue transition-colors placeholder:text-white/30 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={submitting}
                  className="w-full bg-ng-blue text-white px-6 py-4 uppercase tracking-[0.22em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors flex items-center justify-center gap-3 disabled:opacity-60 shadow-[0_0_40px_-8px_rgba(30,144,255,0.7)]"
                >
                  {submitting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      Request Consultation
                      <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Input({ label, value, onChange, type = "text", testId, required, placeholder }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-ng-line text-white py-3 focus:outline-none focus:border-ng-blue transition-colors placeholder:text-white/30"
      />
    </div>
  );
}
